import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useManagerTheme } from '../context/ManagerThemeContext';
import api from '../services/api';
import ManagerSidebar from '../components/ManagerSidebar';
import ManagerHeader from '../components/ManagerHeader';
import ManagerTransferModal from '../components/ManagerTransferModal';
import Footer from '../components/Footer';
import { scheduleLoadingEnd } from '../utils/loadingDelay';

interface DashboardStats {
  managerWalletBalance: number;
  managerAccountBalance: number;
  walletBalance: number;
  totalMerchantsBalance: number;
  totalEscrowBalance: number;
  totalTransactionFees: number;
  totalFailedPayoutBalance: number;
  sparkupCredit: number;
  todayCredits: number;
  todayDebits: number;
  todayCharges: number;
  todayTransactions?: number;
  totalCredits: number;
  totalDebits: number;
  totalUsers: number;
  activeUsers: number;
  totalMerchants: number;
  totalManagers: number;
  totalAdmins: number;
  totalPayouts: number;
  pendingPayouts: number;
  completedPayouts: number;
  failedPayouts: number;
}

const ManagerDashboardNew: React.FC = () => {
  const { user, logout } = useAuth();
  const { sidebarColor, contentColor, sectionColor } = useManagerTheme();
  const [stats, setStats] = useState<DashboardStats>({
    managerWalletBalance: 0,
    managerAccountBalance: 0,
    walletBalance: 0,
    totalMerchantsBalance: 0,
    totalEscrowBalance: 0,
    totalTransactionFees: 0,
    totalFailedPayoutBalance: 0,
    sparkupCredit: 0,
    todayCredits: 0,
    todayDebits: 0,
    todayCharges: 0,
    todayTransactions: 0,
    totalCredits: 0,
    totalDebits: 0,
    totalUsers: 0,
    activeUsers: 0,
    totalMerchants: 0,
    totalManagers: 0,
    totalAdmins: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    completedPayouts: 0,
    failedPayouts: 0,
  });
  const COMPACT_STORAGE_KEY = 'yuvanapi-manager-dashboard-compact';
  const loadingStartRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [accountBalanceCoins, setAccountBalanceCoins] = useState<number | null>(null);
  const [compactMode, setCompactMode] = useState(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return window.localStorage.getItem(COMPACT_STORAGE_KEY) === 'true';
      }
    } catch {
      // ignore
    }
    return false;
  });

  const setCompactModePersisted = (value: boolean | ((prev: boolean) => boolean)) => {
    setCompactMode((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(COMPACT_STORAGE_KEY, String(next));
        }
      } catch {
        // ignore
      }
      return next;
    });
  };

  useEffect(() => {
    fetchDashboardData();
    (async () => {
      try {
        const profileRes = await api.get('/auth/profile');
        const coins = parseFloat(profileRes?.data?.data?.balance) || 0;
        setAccountBalanceCoins(Math.floor(coins));
      } catch (_) {}
    })();
  }, []);

  const fetchDashboardData = async () => {
    try {
      loadingStartRef.current = Date.now();
      setLoading(true);
      const response = await api.get('/dashboard/manager/stats');
      const managerStats = response.data.data;
      setStats({
        managerWalletBalance: parseFloat(managerStats.managerWalletBalance) || 0,
        managerAccountBalance: parseFloat(managerStats.managerAccountBalance) || 0,
        walletBalance: parseFloat(managerStats.walletBalance) || 0,
        totalMerchantsBalance: parseFloat(managerStats.totalMerchantsBalance) || 0,
        totalEscrowBalance: parseFloat(managerStats.totalEscrowBalance) || 0,
        totalTransactionFees: parseFloat(managerStats.totalTransactionFees) || 0,
        totalFailedPayoutBalance: parseFloat(managerStats.totalFailedPayoutBalance) || 0,
        sparkupCredit: parseFloat(managerStats.sparkupCredit) || 0,
        todayCredits: parseFloat(managerStats.todayCredits) || 0,
        todayDebits: parseFloat(managerStats.todayDebits) || 0,
        todayCharges: parseFloat(managerStats.todayCharges) || 0,
        todayTransactions: parseInt(managerStats.todayTransactions) || 0,
        totalCredits: parseFloat(managerStats.totalCredits) || 0,
        totalDebits: parseFloat(managerStats.totalDebits) || 0,
        totalUsers: parseInt(managerStats.totalUsers) || 0,
        activeUsers: parseInt(managerStats.activeUsers) || 0,
        totalMerchants: parseInt(managerStats.totalMerchants) || 0,
        totalManagers: parseInt(managerStats.totalManagers) || 0,
        totalAdmins: parseInt(managerStats.totalAdmins) || 0,
        totalPayouts: parseInt(managerStats.totalPayouts) || 0,
        pendingPayouts: parseInt(managerStats.pendingPayouts) || 0,
        completedPayouts: parseInt(managerStats.completedPayouts) || 0,
        failedPayouts: parseInt(managerStats.failedPayouts) || 0,
      });
    } catch (error: unknown) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      scheduleLoadingEnd(loadingStartRef.current, setLoading);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount);

  const closeMobile = () => setShowMobileSidebar(false);

  const mobileNavItems = [
    { href: '/manager', label: 'Home', icon: '🏠' },
    { href: '/manager/account', label: 'My Account', icon: '👤' },
    { href: '/manager/payouts', label: 'Payouts', icon: '💰' },
    { href: '/manager/reports', label: 'Reports', icon: '📊' },
    { href: '/manager/merchants', label: 'My Merchants', icon: '🏪' },
    { href: '/manager/merchant-report-history', label: 'Merchant Reports', icon: '💳' },
    { href: '/manager/settings', label: 'Settings', icon: '⚙️' },
  ];

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-slate-400 backdrop-blur-sm" style={{ backgroundColor: contentColor }}>
        <div className="w-10 h-10 border-2 border-slate-600 border-t-violet-500 rounded-full animate-spin mb-4" />
        <p className="text-slate-400 text-sm font-medium m-0">Loading dashboard...</p>
      </div>
    );
  }

  /* Attractive card style: gradient, glow, hover lift */
  const cardBase = compactMode
    ? 'rounded-2xl p-3 flex items-center justify-center gap-3 bg-gradient-to-br from-[#353548] to-[#262633] border border-white/[0.06] hover:border-white/[0.12] hover:shadow-lg hover:shadow-violet-500/5 transition-all duration-300 min-h-[72px] shadow-xl shadow-black/25'
    : 'rounded-2xl p-5 flex items-center gap-4 bg-gradient-to-br from-[#353548] to-[#262633] border border-white/[0.06] hover:border-white/[0.12] hover:-translate-y-0.5 hover:shadow-xl hover:shadow-violet-500/5 transition-all duration-300 shadow-xl shadow-black/25';
  /* Compact: more columns = narrower cards */
  const gridColsFinancial = compactMode ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2';
  const gridColsDaily = compactMode ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
  const gridColsUser = compactMode ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  const gridColsPayout = compactMode ? 'grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  const cardIconBase = compactMode
    ? 'w-9 h-9 rounded-xl flex items-center justify-center text-base flex-shrink-0 ring-2 ring-white/5 shadow-inner'
    : 'w-12 h-12 rounded-xl flex items-center justify-center text-lg flex-shrink-0 ring-2 ring-white/5 shadow-inner';
  const cardContentBase = compactMode
    ? 'flex flex-col gap-0.5 min-w-0 items-center text-center justify-center'
    : 'flex flex-col gap-0.5 min-w-0';
  const cardLabel = compactMode
    ? 'text-[10px] font-medium uppercase tracking-widest text-slate-400 m-0 mb-0'
    : 'text-[11px] font-medium uppercase tracking-widest text-slate-400 m-0 mb-0.5';
  const cardValue = compactMode
    ? 'text-base font-bold text-white m-0 leading-tight tabular-nums'
    : 'text-xl font-bold text-white m-0 leading-tight tabular-nums';
  const cardSub = compactMode ? 'text-[11px] text-slate-500 m-0' : 'text-xs text-slate-500 m-0';
  const sectionGap = compactMode ? 'gap-3' : 'gap-4';
  const sectionTitleMb = compactMode ? 'mb-3' : 'mb-4';
  const sectionPadding = compactMode ? 'p-4 md:p-5' : 'p-5 md:p-6';
  const contentGap = compactMode ? 'gap-5' : 'gap-8';
  const welcomeCompact = compactMode ? 'pb-4' : 'pb-5';

  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden text-slate-200" style={{ backgroundColor: contentColor }}>
      {/* Mobile toggle */}
      <button
        type="button"
        className="hidden max-md:flex fixed top-4 right-4 z-[9999] w-11 h-11 rounded-xl bg-[#2D2D3A] border border-white/[0.08] text-slate-300 text-xl cursor-pointer shadow-lg items-center justify-center hover:bg-[#36364a] transition-colors"
        onClick={() => setShowMobileSidebar(!showMobileSidebar)}
        aria-label="Toggle sidebar"
      >
        ☰
      </button>

      {showMobileSidebar && (
        <div
          className="fixed inset-0 bg-black/60 z-[1002] backdrop-blur-sm max-md:block hidden"
          onClick={closeMobile}
          aria-hidden
        />
      )}

      {/* Mobile sidebar drawer */}
      <div
        className={`fixed top-0 left-0 w-[280px] h-screen border-r border-white/[0.08] z-[1003] shadow-xl transition-transform duration-300 overflow-y-auto max-md:block hidden ${
          showMobileSidebar ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ backgroundColor: sidebarColor }}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/[0.08]">
          <h3 className="m-0 text-lg font-semibold text-white">Menu</h3>
          <button
            type="button"
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#2D2D3A] text-slate-400 hover:text-white transition-colors border-none cursor-pointer"
            onClick={closeMobile}
          >
            ✕
          </button>
        </div>
        <nav className="flex flex-col py-2">
          {mobileNavItems.map(({ href, label, icon }) => (
            <button
              key={href}
              type="button"
              className="flex items-center gap-4 py-3.5 px-5 w-full text-left border-none bg-transparent cursor-pointer transition-colors hover:bg-[#2D2D3A]/80 text-slate-400 hover:text-white"
              onClick={() => {
                closeMobile();
                window.location.href = href;
              }}
            >
              <span className="text-lg w-7 text-center">{icon}</span>
              <span className="text-sm font-medium">{label}</span>
            </button>
          ))}
          <button
            type="button"
            className="flex items-center gap-4 py-3.5 px-5 w-full text-left border-none bg-transparent cursor-pointer hover:bg-emerald-500/10 text-slate-400 hover:text-emerald-400 mt-4 pt-4 border-t border-white/[0.08]"
            onClick={() => {
              setShowTransferModal(true);
              closeMobile();
            }}
          >
            <span className="text-lg w-7 text-center">💵</span>
            <span className="text-sm font-medium">Credit Money</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-4 py-3.5 px-5 w-full text-left border-none bg-transparent cursor-pointer hover:bg-red-500/10 text-slate-400 hover:text-red-400"
            onClick={() => {
              logout();
              closeMobile();
            }}
          >
            <span className="text-lg w-7 text-center">🚪</span>
            <span className="text-sm font-medium">Logout</span>
          </button>
        </nav>
      </div>

      <ManagerHeader
        user={user}
        onRefresh={fetchDashboardData}
        onCreditMoney={() => setShowTransferModal(true)}
        onMyProfile={() => (window.location.href = '/manager/account')}
        onLogout={() => logout()}
      />

      <div className="flex flex-1 min-h-0 w-full">
        {/* Sidebar: fixed-width left column, its own space */}
        <div className="max-md:hidden w-[280px] flex-shrink-0 flex flex-col border-r border-white/[0.08]" style={{ backgroundColor: sidebarColor }}>
          <ManagerSidebar activeTab="home" />
        </div>

        {/* Dashboard: main content column, does not overlap sidebar */}
        <div className={`flex-1 min-w-0 flex flex-col w-full max-md:w-full p-6 md:p-8 text-slate-200 ${contentGap}`} style={{ backgroundColor: contentColor }}>
          {/* Welcome - fintech hero */}
          <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-white/[0.08] ${welcomeCompact}`}>
            <div className="pb-2">
              <h1 className={`font-semibold text-white m-0 mb-1 tracking-tight ${compactMode ? 'text-lg md:text-xl' : 'text-xl md:text-2xl'}`}>
                Welcome back, {user?.name || 'Manager'}
              </h1>
              <p className={`text-slate-500 m-0 ${compactMode ? 'text-xs' : 'text-sm'}`}>YuvanAPI Manager Portal</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className={`rounded-2xl bg-gradient-to-br from-[#353548] to-[#262633] border border-white/[0.06] shadow-xl shadow-black/25 mb-5 ${compactMode ? 'px-3 py-1.5' : 'px-4 py-2.5'}`}>
                <p className={`font-medium uppercase tracking-widest text-slate-400 m-0 mb-0.5 ${compactMode ? 'text-[10px]' : 'text-[11px]'}`}>Account balance</p>
                <p className={`font-semibold text-white m-0 tabular-nums ${compactMode ? 'text-sm' : 'text-base'}`}>{formatCurrency(stats.managerAccountBalance)}</p>
              </div>
              <div className={`rounded-2xl bg-gradient-to-br from-[#353548] to-[#262633] border border-white/[0.06] shadow-xl shadow-black/25 mb-5 ${compactMode ? 'px-3 py-1.5' : 'px-4 py-2.5'}`}>
                <p className={`font-medium uppercase tracking-widest text-slate-400 m-0 mb-0.5 ${compactMode ? 'text-[10px]' : 'text-[11px]'}`}>Coin wallet</p>
                <p className={`font-semibold text-white m-0 tabular-nums ${compactMode ? 'text-sm' : 'text-base'}`}>
                  {accountBalanceCoins != null
                    ? Math.floor(accountBalanceCoins).toLocaleString()
                    : Math.floor(stats.managerWalletBalance || 0).toLocaleString()}{' '}
                  <span className="text-slate-500 font-normal text-sm">Coin</span>
                </p>
              </div>
            </div>
          </div>

          {/* Financial Overview */}
          <section
            className={`rounded-2xl border-2 border-violet-500/40 shadow-xl ${sectionPadding}`}
            style={{
              backgroundColor: sectionColor,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2), 0 0 30px -5px rgba(139, 92, 246, 0.15)',
            }}
          >
            <div className={`flex flex-row items-center justify-between gap-4 ${sectionTitleMb}`}>
              <div className="text-left">
                <h2 className={`font-semibold text-white m-0 mb-0.5 ${compactMode ? 'text-sm' : 'text-base'}`}>Financial overview</h2>
                <p className="text-xs text-slate-500 m-0">Current balances and status</p>
              </div>
              <button
                type="button"
                className={`flex items-center gap-2 rounded-2xl border px-3 py-2 transition-all duration-300 flex-shrink-0 ml-auto ${compactMode ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-400 shadow-lg shadow-emerald-500/10' : 'border-white/[0.06] bg-gradient-to-br from-[#353548] to-[#262633] text-slate-400 hover:text-slate-300 hover:border-white/[0.12] hover:shadow-lg hover:shadow-violet-500/5'}`}
                onClick={() => setCompactModePersisted((prev) => !prev)}
                title={compactMode ? 'Currently: Compact mode' : 'Currently: Standard mode'}
                aria-pressed={compactMode}
              >
                <span className={compactMode ? 'text-sm' : 'text-base'} aria-hidden>⊡</span>
                <span className="text-xs font-medium whitespace-nowrap">
                  {compactMode ? 'Compact mode' : 'Standard mode'}
                </span>
              </button>
            </div>
            <div className={`grid ${gridColsFinancial} ${sectionGap}`}>
              <div className={cardBase}>
                <div className={`${cardIconBase} bg-gradient-to-br from-[#404050] to-[#343442] text-slate-300`}>💼</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Account balance</h3>
                  <p className={cardValue}>{formatCurrency(stats.managerAccountBalance)}</p>
                  <p className={cardSub}>Your personal wallet</p>
                </div>
              </div>
              <div className={`${cardBase} border-l-2 border-l-emerald-500/60`}>
                <div className={`${cardIconBase} bg-emerald-500/25 text-emerald-400 shadow-lg shadow-emerald-500/15`}>↑</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Total credit</h3>
                  <p className={`${cardValue} text-emerald-400`}>{formatCurrency(stats.totalCredits)}</p>
                  <p className={cardSub}>Credits to merchants</p>
                </div>
              </div>
              <div className={`${cardBase} border-l-2 border-l-red-500/60`}>
                <div className={`${cardIconBase} bg-red-500/25 text-red-400 shadow-lg shadow-red-500/15`}>↓</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Total debit</h3>
                  <p className={`${cardValue} text-red-400`}>{formatCurrency(stats.totalDebits)}</p>
                  <p className={cardSub}>Debits to merchants</p>
                </div>
              </div>
              <div className={cardBase}>
                <div className={`${cardIconBase} bg-gradient-to-br from-[#404050] to-[#343442] text-slate-300`}>💳</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Transaction fees</h3>
                  <p className={cardValue}>{formatCurrency(stats.totalTransactionFees)}</p>
                  <p className={cardSub}>Service charges</p>
                </div>
              </div>
            </div>
          </section>

          {/* Daily Transactions */}
          <section
            className={`rounded-2xl border-2 border-emerald-500/40 shadow-xl ${sectionPadding}`}
            style={{
              backgroundColor: sectionColor,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2), 0 0 30px -5px rgba(16, 185, 129, 0.15)',
            }}
          >
            <div className={`text-left ${sectionTitleMb}`}>
              <h2 className={`font-semibold text-white m-0 mb-0.5 ${compactMode ? 'text-sm' : 'text-base'}`}>Daily transactions</h2>
              <p className="text-xs text-slate-500 m-0">Today’s summary</p>
            </div>
            <div className={`grid ${gridColsDaily} ${sectionGap}`}>
              <div className={`${cardBase} border-l-2 border-l-emerald-500/60`}>
                <div className={`${cardIconBase} bg-emerald-500/25 text-emerald-400 shadow-lg shadow-emerald-500/15`}>↑</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Today credits</h3>
                  <p className={cardValue}>{formatCurrency(stats.todayCredits)}</p>
                  <p className="text-xs font-medium text-emerald-500 m-0 mt-0.5">+8.2%</p>
                </div>
              </div>
              <div className={`${cardBase} border-l-2 border-l-red-500/60`}>
                <div className={`${cardIconBase} bg-red-500/25 text-red-400 shadow-lg shadow-red-500/15`}>↓</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Today debits</h3>
                  <p className={cardValue}>{formatCurrency(stats.todayDebits)}</p>
                  <p className="text-xs font-medium text-red-500 m-0 mt-0.5">-3.1%</p>
                </div>
              </div>
              <div className={cardBase}>
                <div className={`${cardIconBase} bg-gradient-to-br from-[#404050] to-[#343442] text-slate-300`}>📋</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Today charges</h3>
                  <p className={cardValue}>{formatCurrency(stats.todayCharges)}</p>
                  <p className={cardSub}>Service fees</p>
                </div>
              </div>
              <div className={cardBase}>
                <div className={`${cardIconBase} bg-gradient-to-br from-[#404050] to-[#343442] text-slate-300`}>📄</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Transaction count</h3>
                  <p className={cardValue}>{stats.todayTransactions ?? 0}</p>
                  <p className={cardSub}>Today</p>
                </div>
              </div>
            </div>
          </section>

          {/* User Management */}
          <section
            className={`rounded-2xl border-2 border-sky-500/40 shadow-xl ${sectionPadding}`}
            style={{
              backgroundColor: sectionColor,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2), 0 0 30px -5px rgba(14, 165, 233, 0.15)',
            }}
          >
            <div className={`text-left ${sectionTitleMb}`}>
              <h2 className={`font-semibold text-white m-0 mb-0.5 ${compactMode ? 'text-sm' : 'text-base'}`}>User management</h2>
              <p className="text-xs text-slate-500 m-0">Platform statistics</p>
            </div>
            <div className={`grid ${gridColsUser} ${sectionGap}`}>
              {[
                { icon: '👤', title: 'Total users', value: stats.totalUsers, sub: 'Registered' },
                { icon: '✅', title: 'Active users', value: stats.activeUsers, sub: '+5.7%', trend: 'positive' },
                { icon: '👔', title: 'Managers', value: stats.totalManagers, sub: 'Registered' },
                { icon: '🏪', title: 'Merchants', value: stats.totalMerchants, sub: 'Registered' },
                { icon: '👑', title: 'Admins', value: stats.totalAdmins, sub: 'System' },
              ].map((item) => (
                <div key={item.title} className={cardBase}>
                  <div className={`${cardIconBase} bg-gradient-to-br from-[#404050] to-[#343442] text-slate-300`}>{item.icon}</div>
                  <div className={cardContentBase}>
                    <h3 className={cardLabel}>{item.title}</h3>
                    <p className={cardValue}>{item.value}</p>
                    <p className={item.trend ? `text-xs font-medium m-0 mt-0.5 ${item.trend === 'positive' ? 'text-emerald-500' : 'text-red-500'}` : cardSub}>
                      {item.sub}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Payout Management */}
          <section
            className={`rounded-2xl border-2 border-amber-500/40 shadow-xl ${sectionPadding}`}
            style={{
              backgroundColor: sectionColor,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2), 0 0 30px -5px rgba(245, 158, 11, 0.15)',
            }}
          >
            <div className={`text-left ${sectionTitleMb}`}>
              <h2 className={`font-semibold text-white m-0 mb-0.5 ${compactMode ? 'text-sm' : 'text-base'}`}>Payout management</h2>
              <p className="text-xs text-slate-500 m-0">Status and counts</p>
            </div>
            <div className={`grid ${gridColsPayout} ${sectionGap}`}>
              <div className={cardBase}>
                <div className={`${cardIconBase} bg-gradient-to-br from-[#404050] to-[#343442] text-slate-300`}>💸</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Total payouts</h3>
                  <p className={cardValue}>{stats.totalPayouts}</p>
                  <p className={cardSub}>All transactions</p>
                </div>
              </div>
              <div className={`${cardBase} border-l-2 border-l-amber-500/60`}>
                <div className={`${cardIconBase} bg-amber-500/25 text-amber-400 shadow-lg shadow-amber-500/15`}>⏳</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Pending · Failed</h3>
                  <p className={cardValue}>{stats.pendingPayouts} · {stats.failedPayouts}</p>
                  <p className={cardSub}>Refund needed</p>
                </div>
              </div>
              <div className={`${cardBase} border-l-2 border-l-emerald-500/60`}>
                <div className={`${cardIconBase} bg-emerald-500/25 text-emerald-400 shadow-lg shadow-emerald-500/15`}>✓</div>
                <div className={cardContentBase}>
                  <h3 className={cardLabel}>Completed</h3>
                  <p className={cardValue}>{stats.completedPayouts}</p>
                  <p className={cardSub}>Processed</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <ManagerTransferModal isOpen={showTransferModal} onClose={() => setShowTransferModal(false)} onTransferSuccess={fetchDashboardData} />
      <Footer />
    </div>
  );
};

export default ManagerDashboardNew;
